import { UserDto, userService } from "@/services/userService";
import {
  ApiResponse,
  COMMON_STATUS,
  CommonParams,
  CreateUserDto,
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_SUCCESS_MESSAGE,
  UpdateUserDto,
  UserInfor,
  UserInStore,
  UserLessonDto,
  UserQuizDto,
  type User,
} from "@/types";
import { toastError, toastSuccess } from "@/utils/toastify";
import { toast } from "react-toastify";
import { create } from "zustand";

export interface UserStore {
  // State
  users: User[];
  usersInStore: ApiResponse<UserInStore[]>;
  currentUser: User | UserInfor | UserDto | null;
  currentUserInfor: UserInfor | UserDto | null;
  userLessons: ApiResponse<UserLessonDto[] | null>;
  userQuizzes: ApiResponse<UserQuizDto[] | null>;
  loading: boolean;
  error: string | null;

  // Actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setUsers: (users: User[]) => void;
  setUsersInStore: (usersInStore: ApiResponse<UserInStore[]>) => void;
  setCurrentUser: (user: User | UserDto | null) => void;
  setCurrentUserInfor: (user: UserInfor | UserDto | null) => void;
  setUserLessons: (userLessons: ApiResponse<UserLessonDto[] | null>) => void;
  setUserQuizzes: (userQuizzes: ApiResponse<UserQuizDto[] | null>) => void;
  // Async actions
  fetchUsers: () => Promise<User[]>;
  fetchUserById: (id: number) => Promise<User | null>;
  fetchMyProfile: () => Promise<User | UserDto>;
  createUser: (userData: Partial<CreateUserDto>) => Promise<User>;
  updateUser: (id: number, userData: Partial<User>) => Promise<User>;
  updateUserInfor: (
    id: number,
    userData: Partial<UpdateUserDto>,
  ) => Promise<UserInfor>;
  resendInfor: (id: number) => Promise<null>;
  changeAvatar: (id: number, data: { file: File | null }) => Promise<null>;
  deleteUser: (id: number) => Promise<void>;
  fetchUsersInStore: (
    params: CommonParams<{
      id: string;
    }>,
  ) => Promise<ApiResponse<UserInStore[]>>;
  fetchMyUsersStore: (
    params: CommonParams<{
      id: string;
    }>,
  ) => Promise<ApiResponse<UserInStore[]>>;
  updateUserStatus: (
    id: number,
  ) => Promise<ApiResponse<{ status: "ENABLE" | "DISABLE" }>>;
  // Reset functions
  clearError: () => void;
  clearCurrentUser: () => void;
  resetStore: () => void;
  fetchUserLessons: (id: number, params: CommonParams<{}>) => Promise<ApiResponse<UserLessonDto[]>>;
  fetchUserQuizzes: (id: number, params: CommonParams<{}>) => Promise<ApiResponse<UserQuizDto[]>>;
}

let requestId = 0;

export const useUserStore = create<UserStore>((set, get) => ({
  // State
  users: [],
  usersInStore: { data: [], status: 200 },
  currentUser: null,
  currentUserInfor: null,
  loading: false,
  error: null,
  userLessons: { data: null, status: 200 },
  userQuizzes: { data: null, status: 200 },

  // Actions
  setLoading: (loading: boolean) => set({ loading }),
  setError: (error: string | null) => set({ error }),
  setUsers: (users: User[]) => set({ users }),
  setUsersInStore: (usersInStore) => set({ usersInStore }),
  setCurrentUser: (user: User | UserDto | null) => set({ currentUser: user }),
  setCurrentUserInfor: (user: UserInfor | UserDto | null) =>
    set({ currentUserInfor: user }),
  setUserLessons: (userLessons: ApiResponse<UserLessonDto[] | null>) => set({ userLessons }),
  setUserQuizzes: (userQuizzes: ApiResponse<UserQuizDto[] | null>) => set({ userQuizzes }),

  // Async actions
  fetchUsers: async (): Promise<User[]> => {
    set({ loading: true, error: null });
    try {
      const users = await userService.getUsers();
      set({ users, loading: false });
      toast.success(`✅ Loaded ${users.length} users successfully!`);
      return users;
    } catch (error: any) {
      const errorMsg = error.message || "Failed to fetch users";
      set({
        error: errorMsg,
        loading: false,
      });
      toast.error(`❌ ${errorMsg}`);
      throw error;
    }
  },

  fetchUserById: async (id: number) => {
    set({ loading: true, error: null });
    try {
      const user = await userService.getUserById(id);
      set({ currentUser: user, currentUserInfor: user, loading: false });
      return user;
    } catch (error: any) {
      const errorMsg = error.message || DEFAULT_ERROR_MESSAGE;
      set({
        error: errorMsg,
        loading: false,
      });
      toastError(errorMsg);
      throw error;
    }
  },

  fetchMyProfile: async () => {
    set({ loading: true, error: null });
    try {
      const user = await userService.getUser();
      set({ currentUser: user, currentUserInfor: user, loading: false });
      return user;
    } catch (error: any) {
      const errorMsg = error.message || DEFAULT_ERROR_MESSAGE;
      set({
        error: errorMsg,
        loading: false,
      });
      toastError(errorMsg);
      throw error;
    }
  },

  createUser: async (userData: Partial<CreateUserDto>): Promise<User> => {
    try {
      const resp = await userService.createUser(userData);
      toastSuccess(resp.message || DEFAULT_SUCCESS_MESSAGE);
      return resp.data;
    } catch (error: any) {
      const errorMsg = error.message || DEFAULT_ERROR_MESSAGE;
      toastError(errorMsg);
      throw error;
    }
  },

  updateUser: async (id: number, userData: Partial<User>): Promise<User> => {
    set({ loading: true, error: null });
    try {
      const updatedUser = await userService.updateUser(id, userData);
      const { users } = get();
      set({
        users: users.map((user) => (user.id === id ? updatedUser : user)),
        loading: false,
      });
      toast.success(
        `✅ User "${updatedUser?.full_name}" updated successfully!`,
      );
      return updatedUser;
    } catch (error: any) {
      const errorMsg = error.message || "Failed to update user";
      set({
        error: errorMsg,
        loading: false,
      });
      toast.error(`❌ ${errorMsg}`);
      throw error;
    }
  },

  updateUserInfor: async (id: number, userData: Partial<UpdateUserDto>) => {
    try {
      const { data, message } = await userService.updateUserInfor(id, userData);

      const updatedUser = {
        ...data,
        company: {
          id: data.company_id,
        },
        store: {
          id: data.store_id,
        },
        groups: userData.group_ids?.map((group_id) => ({
          id: group_id,
          group_id: group_id,
        })),
      };
      set({
        currentUserInfor: updatedUser as any,
      });
      toastSuccess(message || DEFAULT_SUCCESS_MESSAGE);
      return updatedUser;
    } catch (error: any) {
      const errorMsg = error.message || DEFAULT_ERROR_MESSAGE;
      set({
        error: errorMsg,
      });
      toastError(errorMsg);
      throw error;
    }
  },

  resendInfor: async (id: number) => {
    try {
      const { data, message } = await userService.resendInfor(id);

      toastSuccess(message || DEFAULT_SUCCESS_MESSAGE);
      return data;
    } catch (error: any) {
      const errorMsg = error.message || DEFAULT_ERROR_MESSAGE;
      set({
        error: errorMsg,
      });
      toastError(errorMsg);
      throw error;
    }
  },

  changeAvatar: async (id: number, fileData: { file: File | null }) => {
    try {
      const { data } = await userService.changeAvatar(id, fileData);
      // TODO: Disable at the moment, can enable if needed
      // toastSuccess(message || DEFAULT_SUCCESS_MESSAGE);
      return data;
    } catch (error: any) {
      const errorMsg = error.message || DEFAULT_ERROR_MESSAGE;
      set({
        error: errorMsg,
      });
      toastError(errorMsg);
      throw error;
    }
  },

  deleteUser: async (id: number): Promise<void> => {
    set({ loading: true, error: null });
    try {
      const resp = await userService.deleteUser(id);
      const { users, usersInStore } = get();
      set({
        users: users.filter((user) => user.id !== id),
        usersInStore: {
          ...usersInStore,
          data: usersInStore.data.filter((user) => user.id !== id),
        },
        loading: false,
      });
      toastSuccess(resp.message || DEFAULT_SUCCESS_MESSAGE);
    } catch (error: any) {
      set({
        error: error?.message,
        loading: false,
      });
      toastError(error?.message);
      throw error;
    }
  },

  fetchUsersInStore: async (
    params: CommonParams<{ id: string }>
  ): Promise<ApiResponse<UserInStore[]>> => {
    const currentRequestId = ++requestId;

    set({ loading: true, error: null });
    try {
      const usersInStore = await userService.getUsersInStore(params, params.id);

      if (currentRequestId === requestId) {
        set({ usersInStore, loading: false });
      }

      return usersInStore;
    } catch (error: any) {
      if (currentRequestId === requestId) {
        const errorMsg = error.message || DEFAULT_ERROR_MESSAGE;
        set({
          error: errorMsg,
          loading: false,
        });
        toastError(errorMsg);
      }
      throw error;
    }
  },

  fetchMyUsersStore: async (
    params: CommonParams<{ id: string }>,
  ): Promise<ApiResponse<UserInStore[]>> => {
    set({ loading: true, error: null });
    try {
      const usersInStore = await userService.getMyUsers(params);
      set({ usersInStore, loading: false });
      return usersInStore;
    } catch (error: any) {
      const errorMsg = error.message || DEFAULT_ERROR_MESSAGE;
      set({
        error: errorMsg,
        loading: false,
      });
      toastError(errorMsg);
      throw error;
    }
  },


  updateUserStatus: async (id: number) => {
    const currentUsersInStore = get().usersInStore;
    try {
      const updatedUsersInStore = {
        ...currentUsersInStore,
        data: currentUsersInStore.data.map((user) =>
          user.id === id
            ? {
                ...user,
          status: user.status === COMMON_STATUS.ENABLE ? COMMON_STATUS.DISABLE : COMMON_STATUS.ENABLE 
              }
        : user,
        ),
      };

      set({
        usersInStore: updatedUsersInStore,
        loading: false,
      });
      const response = await userService.updateUserStatus(id);
      toastSuccess(response.message || DEFAULT_SUCCESS_MESSAGE);

      return response;
    } catch (error: any) {
      const errorMsg = error.message || DEFAULT_ERROR_MESSAGE;
      set({
        error: errorMsg,
        loading: false,
        usersInStore: currentUsersInStore,
      });
      toastError(errorMsg);
      throw error;
    }
  },

  fetchUserLessons: async (id: number, params: CommonParams<{}>) => {
    set({ loading: true, error: null });
    try {
      const response = await userService.getUserLessons(id, params);
      set({ userLessons: response, loading: false });
      return response;
    } catch (error: any) {
      const errorMsg = error.message || DEFAULT_ERROR_MESSAGE;
      set({
        error: errorMsg,
        loading: false,
      });
      toastError(errorMsg);
      return { data: [], status: error?.status };
    }
  },

  fetchUserQuizzes: async (id: number, params: CommonParams<{}>) => {
    set({ loading: true, error: null });
    try {
      const response = await userService.getUserQuizzes(id, params);
      set({ userQuizzes: response, loading: false });
      return response;
    } catch (error: any) {
      const errorMsg = error.message || DEFAULT_ERROR_MESSAGE;
      set({
        error: errorMsg,
        loading: false,
      });
      toastError(errorMsg);
      return { data: [], status: error?.status };
    }
  },

  // Reset functions
  clearError: () => set({ error: null }),
  clearCurrentUser: () => set({ currentUser: null }),
  resetStore: () =>
    set({
      users: [],
      usersInStore: { data: [], status: 200 },
      currentUser: null,
      loading: false,
      error: null,
    }),
}));
